import React, { useState, useEffect, useRef } from "react";
import "./App.css";

// Generate unique ID for each message
function generateId() {
  return Math.random().toString(36).substring(2, 9);
}

// Get local date/time in 12-hour format with AM/PM
function getLocalDateTimeString() {
  const now = new Date();
  return now.toLocaleString("en-US", {
    dateStyle: "short",
    timeStyle: "medium", // includes AM/PM
  });
}

// Simple login screen: user enters a code
function SecretCodeScreen({ onSubmit }) {
  const [code, setCode] = useState("");

  const handleEnter = () => {
    onSubmit(code.trim());
  };

  return (
    <div className="secret-code-container">
      <h2>Enter Your Secret Code</h2>
      <input
        type="password"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder="Enter code..."
      />
      <button onClick={handleEnter}>Enter</button>
    </div>
  );
}

function App() {
  // ----------------------------------------------------------------
  // 1) Roles, Codes, Authorization
  // ----------------------------------------------------------------
  const [role, setRole] = useState(null); // "admin" or "regular"
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [adminSecretCode, setAdminSecretCode] = useState("my-admin-code");
  const [regularSecretCode, setRegularSecretCode] = useState("my-regular-code");

  // ----------------------------------------------------------------
  // 2) Main App States
  // ----------------------------------------------------------------
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [aiOn, setAiOn] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [showTime, setShowTime] = useState(true);

  const messageEndRef = useRef(null);

  // Settings (Persistent)
  const [systemPrompt, setSystemPrompt] = useState("");
  const [resume, setResume] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [provider, setProvider] = useState("openai");
  const [model, setModel] = useState("gpt-3.5-turbo");
  const [apiKey, setApiKey] = useState("");
  const [token, setToken] = useState(500);
  const [temperature, setTemperature] = useState(0.7);
  const [autoScroll, setAutoScroll] = useState(true);
  const [availableModels, setAvailableModels] = useState([]);

  const MODEL_MAP = {
    openai: [
      "gpt-3.5-turbo",
      "gpt-4",
      "text-davinci-003",
      "text-curie-001",
      "text-babbage-001",
    ],
    deepseek: ["deepseek-chat", "deepseek-reasoner"],
    google: ["gemini-2.0-flash-exp", "gemini-1.5-pro"],
    ollama: [],
  };

  // ----------------------------------------------------------------
  // 3) Inactivity Timeout (15 min)
  // ----------------------------------------------------------------
  const lastInteractionRef = useRef(Date.now());
  const INACTIVITY_LIMIT_MS = 15 * 60 * 1000;

  useEffect(() => {
    const interval = setInterval(() => {
      if (isAuthorized) {
        const now = Date.now();
        const diff = now - lastInteractionRef.current;
        if (diff >= INACTIVITY_LIMIT_MS) {
          handleLogout();
        }
      }
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, [isAuthorized]);

  function updateLastInteraction() {
    lastInteractionRef.current = Date.now();
  }

  // ----------------------------------------------------------------
  // 4) handleLogout
  // ----------------------------------------------------------------
  function handleLogout() {
    localStorage.removeItem("big-joe-authorized");
    localStorage.removeItem("big-joe-user-role");
    setIsAuthorized(false);
    setRole(null);
  }

  // ----------------------------------------------------------------
  // 5) on mount, load localStorage
  // ----------------------------------------------------------------
  useEffect(() => {
    const storedAuth = localStorage.getItem("big-joe-authorized");
    const storedRole = localStorage.getItem("big-joe-user-role");
    if (storedAuth === "true") {
      setIsAuthorized(true);
      setRole(storedRole || "regular");
    }

    const savedSettings = localStorage.getItem("ai-settings");
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setAdminSecretCode(parsed.adminSecretCode || "my-admin-code");
        setRegularSecretCode(parsed.regularSecretCode || "my-regular-code");
        setSystemPrompt(parsed.systemPrompt || "");
        setResume(parsed.resume || "");
        setJobDescription(parsed.jobDescription || "");
        setProvider(parsed.provider || "openai");
        setModel(parsed.model || "gpt-3.5-turbo");
        setApiKey(parsed.apiKey || "");
        if (typeof parsed.token === "number") {
          setToken(parsed.token);
        } else {
          setToken(500);
        }
        setTemperature(parsed.temperature ?? 0.7);
        setAutoScroll(parsed.autoScroll ?? true);
      } catch (err) {
        console.error("Error parsing stored settings:", err);
      }
    }

    const savedMessages = localStorage.getItem("ai-messages");
    if (savedMessages) {
      try {
        const parsedMsgs = JSON.parse(savedMessages);
        if (Array.isArray(parsedMsgs)) {
          setMessages(parsedMsgs);
        }
      } catch (err) {
        console.error("Error parsing stored messages:", err);
      }
    }

    const savedShowTime = localStorage.getItem("showTime");
    if (savedShowTime !== null) {
      setShowTime(JSON.parse(savedShowTime));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("ai-messages", JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    localStorage.setItem("showTime", JSON.stringify(showTime));
  }, [showTime]);

  function saveSettingsToLocalStorage() {
    const settingsObj = {
      adminSecretCode,
      regularSecretCode,
      systemPrompt,
      resume,
      jobDescription,
      provider,
      model,
      apiKey,
      token,
      temperature,
      autoScroll,
    };
    localStorage.setItem("ai-settings", JSON.stringify(settingsObj));
    console.log("Settings saved to localStorage:", settingsObj);
  }

  // ----------------------------------------------------------------
  // 6) Setup for SpeechRecognition
  // ----------------------------------------------------------------
  const recognitionRef = useRef(null);
  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-US";

      recognition.onstart = () => {
        setIsRecording(true);
        updateLastInteraction();
      };
      recognition.onerror = () => setIsRecording(false);
      recognition.onend = () => setIsRecording(false);
      recognition.onstop = () => setIsRecording(false);

      recognition.onresult = (e) => {
        updateLastInteraction();
        let fullTranscript = "";
        for (let i = 0; i < e.results.length; i++) {
          fullTranscript += e.results[i][0].transcript;
        }
        setInputText(fullTranscript.trim());
      };

      recognitionRef.current = recognition;
    } else {
      recognitionRef.current = null;
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
    };
  }, []);

  // ----------------------------------------------------------------
  // 7) Additional Effects: auto-scroll, dynamic models
  // ----------------------------------------------------------------
  useEffect(() => {
    if (autoScroll && messageEndRef.current) {
      messageEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, autoScroll]);

  useEffect(() => {
    if (provider === "ollama") {
      fetchOllamaModels();
    } else {
      setAvailableModels(MODEL_MAP[provider] || []);
    }
  }, [provider]);

  useEffect(() => {
    if (availableModels.length > 0 && !availableModels.includes(model)) {
      setModel(availableModels[0]);
    }
  }, [availableModels, model]);

  async function fetchOllamaModels() {
    try {
      const response = await fetch("http://localhost:11434/api/tags");
      const data = await response.json();
      if (data && data.models && Array.isArray(data.models)) {
        const modelNames = data.models.map((m) => m.name);
        setAvailableModels(modelNames);
      } else {
        setAvailableModels([]);
      }
    } catch (err) {
      console.error("Could not fetch Ollama models:", err);
      setAvailableModels([]);
    }
  }

  // ----------------------------------------------------------------
  // 8) handleSecretCode
  // ----------------------------------------------------------------
  function handleSecretCode(userCode) {
    updateLastInteraction();
    if (userCode === adminSecretCode) {
      setRole("admin");
      setIsAuthorized(true);
      localStorage.setItem("big-joe-authorized", "true");
      localStorage.setItem("big-joe-user-role", "admin");
    } else if (userCode === regularSecretCode) {
      setRole("regular");
      setIsAuthorized(true);
      localStorage.setItem("big-joe-authorized", "true");
      localStorage.setItem("big-joe-user-role", "regular");
    } else {
      alert("Incorrect code. Try again!");
    }
  }

  // ----------------------------------------------------------------
  // 9) Chat & AI Handlers
  // ----------------------------------------------------------------
  const [isProcessing, setIsProcessing] = useState(false);
  const currentRequestControllerRef = useRef(null);

  function handleRecord() {
    updateLastInteraction();
    const recognition = recognitionRef.current;
    if (!recognition) {
      alert("Speech Recognition not supported.");
      return;
    }
    if (isRecording) {
      recognition.stop();
    } else {
      recognition.start();
    }
  }

  function handleClearBox() {
    setInputText("");
  }

  function handleSend() {
    updateLastInteraction();
    if (!inputText.trim()) return;

    if (isProcessing) {
      alert("AI is currently processing. Please wait or press Stop.");
      return;
    }

    const userMessage = {
      id: generateId(),
      role: "user",
      content: inputText,
      dateTime: getLocalDateTimeString(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputText("");

    if (!aiOn) {
      const offMsg = {
        id: generateId(),
        role: "assistant",
        content: "AI is OFF. Your request was not processed.",
        dateTime: getLocalDateTimeString(),
      };
      setMessages((prev) => [...prev, offMsg]);
      return;
    }

    callAiWithContext(userMessage.content, userMessage.id);
  }

  function handleRetry(parentId) {
    updateLastInteraction();
    if (!parentId) return;
    const userMsg = messages.find((m) => m.id === parentId && m.role === "user");
    if (!userMsg) return;

    if (isProcessing) {
      alert("AI is currently processing. Please wait or press Stop.");
      return;
    }

    callAiWithContext(userMsg.content, userMsg.id);
  }

  async function callAiWithContext(userText, parentUserId) {
    updateLastInteraction();
    const startTime = Date.now();
    let responseText = "";
    setIsProcessing(true);

    const controller = new AbortController();
    currentRequestControllerRef.current = controller;

    try {
      const combinedSystemPrompt = `
        ${systemPrompt}
        Resume: ${resume}
        Job Description: ${jobDescription}
      `.trim();

      if (provider === "openai") {
        responseText = await callOpenAIApi(
          apiKey,
          combinedSystemPrompt,
          userText,
          temperature,
          model,
          token,
          controller.signal
        );
      } else if (provider === "deepseek") {
        responseText = await callDeepseekApi(
          apiKey,
          combinedSystemPrompt,
          userText,
          temperature,
          model,
          token,
          controller.signal
        );
      } else if (provider === "google") {
        responseText = await callGoogleApi(
          apiKey,
          combinedSystemPrompt,
          userText,
          temperature,
          model,
          token,
          controller.signal
        );
      } else if (provider === "ollama") {
        responseText = await callOllamaApi(
          apiKey,
          combinedSystemPrompt,
          userText,
          temperature,
          model,
          token,
          controller.signal
        );
      } else {
        responseText = `No recognized provider. Using mock response for: "${userText}"`;
      }
    } catch (err) {
      if (err.name === "AbortError") {
        console.log("AI request was aborted by the user.");
        responseText = "(Request aborted. No response.)";
      } else {
        console.error("Error calling AI API:", err);
        responseText = "There was an error calling the AI API. Check console logs.";
      }
    } finally {
      setIsProcessing(false);
      currentRequestControllerRef.current = null;
    }

    const endTime = Date.now();
    const elapsedSec = ((endTime - startTime) / 1000).toFixed(2);

    const botMessage = {
      id: generateId(),
      role: "assistant",
      content: responseText,
      parentId: parentUserId,
      dateTime: getLocalDateTimeString(),
      elapsedSec,
    };
    setMessages((prev) => [...prev, botMessage]);
  }

  async function callOpenAIApi(apiKey, systemPrompt, userPrompt, temperature, model, token, signal) {
    const url = "https://api.openai.com/v1/chat/completions";
    const body = {
      model,
      temperature,
      max_tokens: token,
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt },
      ],
    };

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify(body),
      signal,
    });

    if (!response.ok) {
      throw new Error(`OpenAI error: ${response.status}`);
    }
    const data = await response.json();
    if (data?.choices?.[0]?.message?.content) {
      return data.choices[0].message.content;
    }
    return "No response from OpenAI.";
  }

  async function callDeepseekApi(apiKey, systemPrompt, userPrompt, temperature, model, token, signal) {
    const url = "https://api.deepseek.com/chat/completions";
    const body = {
      model,
      temperature,
      max_tokens: token,
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt },
      ],
      stream: false,
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify(body),
        signal,
      });

      if (!response.ok) {
        throw new Error(`DeepSeek error: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      if (data?.choices?.[0]?.message?.content) {
        return data.choices[0].message.content;
      }
      return "No response from DeepSeek.";
    } catch (err) {
      console.error("Error calling DeepSeek API:", err);
      throw err;
    }
  }

  async function callGoogleApi(apiKey, systemPrompt, userPrompt, temperature, model, token, signal) {
    console.log("Pretending to call Google Gemini with:", {
      model,
      systemPrompt,
      userPrompt,
      temperature,
      token,
    });
    return `Mock Gemini response (Model=${model}): I see your system info:\n"${systemPrompt}"\n---\nUser asked: "${userPrompt}". (temp=${temperature}, tokens=${token})`;
  }

  async function callOllamaApi(apiKey, systemPrompt, userPrompt, temperature, model, token, signal) {
    const url = "http://localhost:11434/api/chat";
    const template = `{{range .Messages}}{{if eq .Role "system"}}System: {{.Content}}{{else if eq .Role "user"}}User: {{.Content}}{{end}}
{{end}}Assistant:`;

    const messagesForOllama = [
      { role: "system", content: systemPrompt },
      { role: "user", content: userPrompt },
    ];

    const body = {
      model,
      messages: messagesForOllama,
      template,
      stream: false,
      options: {
        temperature,
        num_predict: token,
      },
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
        signal,
      });

      if (!response.ok) {
        throw new Error(`Ollama error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      if (data?.message?.content) {
        return data.message.content;
      }
      return "No response from Ollama.";
    } catch (err) {
      console.error("Error calling Ollama API:", err);
      throw err;
    }
  }

  function handleStopRequest() {
    updateLastInteraction();
    if (currentRequestControllerRef.current) {
      currentRequestControllerRef.current.abort();
    }
  }

  function handleExportChat() {
    updateLastInteraction();
    const dataStr = JSON.stringify(messages, null, 2);
    const blob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "BigJoe_Chat_Export.json";
    link.click();

    URL.revokeObjectURL(url);
  }

  async function handleImportChat(e) {
    updateLastInteraction();
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await file.text();
      const importedMessages = JSON.parse(text);
      if (Array.isArray(importedMessages)) {
        setMessages(importedMessages);
      } else {
        alert("Invalid file. Expected an array of messages.");
      }
    } catch (err) {
      alert("Error reading chat file. See console for details.");
      console.error(err);
    }
    e.target.value = "";
  }

  function handleExportSettings() {
    updateLastInteraction();
    const settingsObj = {
      adminSecretCode,
      regularSecretCode,
      systemPrompt,
      resume,
      jobDescription,
      provider,
      model,
      apiKey,
      token,
      temperature,
      autoScroll,
    };
    const dataStr = JSON.stringify(settingsObj, null, 2);
    const blob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "BigJoe_Settings_Export.json";
    link.click();

    URL.revokeObjectURL(url);
  }

  async function handleImportSettings(e) {
    updateLastInteraction();
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await file.text();
      const importedSettings = JSON.parse(text);
      if (typeof importedSettings !== "object" || Array.isArray(importedSettings)) {
        alert("Invalid file. Expected an object with settings.");
        return;
      }

      setAdminSecretCode(importedSettings.adminSecretCode || "my-admin-code");
      setRegularSecretCode(importedSettings.regularSecretCode || "my-regular-code");
      setSystemPrompt(importedSettings.systemPrompt || "");
      setResume(importedSettings.resume || "");
      setJobDescription(importedSettings.jobDescription || "");
      setProvider(importedSettings.provider || "openai");
      setModel(importedSettings.model || "gpt-3.5-turbo");
      setApiKey(importedSettings.apiKey || "");
      if (typeof importedSettings.token === "number") {
        setToken(importedSettings.token);
      } else {
        setToken(500);
      }
      setTemperature(importedSettings.temperature ?? 0.7);
      setAutoScroll(importedSettings.autoScroll ?? true);

      localStorage.setItem("ai-settings", JSON.stringify(importedSettings));
    } catch (err) {
      alert("Error reading settings file. See console for details.");
      console.error(err);
    }
    e.target.value = "";
  }

  function handleOpenHistory() {
    updateLastInteraction();
    setShowHistory(true);
  }

  function handleCloseHistory() {
    updateLastInteraction();
    setShowHistory(false);
  }

  function handleOpenSettings() {
    updateLastInteraction();
    setShowSettings(true);
  }

  function handleCloseSettings() {
    updateLastInteraction();
    saveSettingsToLocalStorage();
    setShowSettings(false);
  }

  function handleClearChat() {
    updateLastInteraction();
    setMessages([]);
  }

  function handleToggleAI() {
    updateLastInteraction();
    setAiOn((prev) => !prev);
  }

  function handleToggleTime() {
    updateLastInteraction();
    setShowTime((prev) => !prev);
  }

  if (!isAuthorized) {
    return <SecretCodeScreen onSubmit={handleSecretCode} />;
  }

  return (
    <div className="app-container" onClick={updateLastInteraction} onKeyDown={updateLastInteraction}>
      <nav className="navbar">
        <div className="logo-and-title">
          <span className="app-logo" role="img" aria-label="logo">
            🤖
          </span>
          <h1 className="navbar-title">Big Joe AI Interview Assistance</h1>
        </div>

        <div className="navbar-icons">
          <button className="icon-button showtime-btn" title="Toggle Time" onClick={handleToggleTime}>
            🕒 {showTime ? "Hide Time" : "Show Time"}
          </button>

          <label htmlFor="import-chat" className="icon-button import-btn" title="Import Chat">
            📥 Import
          </label>
          <input id="import-chat" type="file" accept=".json" style={{ display: "none" }} onChange={handleImportChat} />

          <button className="icon-button export-btn" onClick={handleExportChat} title="Export Chat">
            📤 Export
          </button>

          <button className="icon-button history-btn" title="History" onClick={handleOpenHistory}>
            ⟳ History
          </button>

          <button className="icon-button settings-btn" title="Settings" onClick={handleOpenSettings}>
            ⚙ Settings
          </button>

          <button className="icon-button logout-btn" title="Logout" onClick={handleLogout}>
            🚪 Logout
          </button>
        </div>
      </nav>

      <main className="chat-area">
        {messages.map((msg) => {
          if (msg.role === "assistant") {
            return (
              <div key={msg.id} className="message assistant">
                <strong>AI Response:</strong>
                <div className="answer-text">{msg.content}</div>
                {showTime && (
                  <div className="timestamp-line">
                    {msg.dateTime} | Response generated in {msg.elapsedSec ? msg.elapsedSec + "s" : "0s"}
                  </div>
                )}
                {msg.parentId && (
                  <div style={{ marginTop: "5px" }}>
                    <button className="retry-button" onClick={() => handleRetry(msg.parentId)}>
                      Retry
                    </button>
                  </div>
                )}
              </div>
            );
          } else {
            return (
              <div key={msg.id} className="message user">
                <strong>Interviewer Question:</strong>
                <div className="answer-text">{msg.content}</div>
                {showTime && <div className="timestamp-line">{msg.dateTime}</div>}
              </div>
            );
          }
        })}

        {isProcessing && (
          <div className="message assistant loading">
            <strong>AI Response:</strong>
            <div className="answer-text">
              ai generating response please wait.
              <span className="gear-icon gear-icon-clockwise">⚙️</span>
              <span className="gear-icon gear-icon-counter">⚙️</span>
            </div>
          </div>
        )}

        <div ref={messageEndRef} />
      </main>

      <div className="bottom-bar">
        <div className="input-wrapper">
          <textarea
            className="chat-input"
            rows={3}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Type your message..."
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                handleSend();
              }
            }}
            disabled={isProcessing}
          />
          <button className="clear-box-btn" onClick={handleClearBox}>
            Clear Box
          </button>
          <button
            className="send-button"
            onClick={handleSend}
            disabled={isProcessing}
            style={{ backgroundColor: "green", color: "white" }}
          >
            Send
          </button>
        </div>

        <div className="bottom-buttons">
          <button className="record-btn" onClick={handleRecord}>
            {isRecording ? "Stop" : "Record"}
          </button>

          <button className={`toggle-ai ${aiOn ? "on" : "off"}`} onClick={handleToggleAI} style={{ backgroundColor: "purple", color: "white" }}>
            AI: {aiOn ? "ON" : "OFF"}
          </button>

          <button className="clear-chat-btn" onClick={handleClearChat}>
            Clear Chat
          </button>

          {isProcessing && (
            <button className="stop-btn" onClick={handleStopRequest}>
              Stop
            </button>
          )}
        </div>
      </div>

      {showSettings && (
        <div className="modal-overlay" onClick={handleCloseSettings}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Settings</h2>

            {role === "admin" ? (
              <>
                <label>Admin Code:</label>
                <input type="password" value={adminSecretCode} onChange={(e) => setAdminSecretCode(e.target.value)} placeholder="Admin code" />

                <label>Regular Code:</label>
                <input type="password" value={regularSecretCode} onChange={(e) => setRegularSecretCode(e.target.value)} placeholder="Regular code" />
              </>
            ) : (
              <>
                <label>Regular Code:</label>
                <input type="password" value={regularSecretCode} onChange={(e) => setRegularSecretCode(e.target.value)} placeholder="Regular code" />
              </>
            )}

            <label>System Prompt:</label>
            <textarea rows={2} value={systemPrompt} onChange={(e) => setSystemPrompt(e.target.value)} />

            <label>Resume:</label>
            <textarea rows={2} value={resume} onChange={(e) => setResume(e.target.value)} />

            <label>Job Description:</label>
            <textarea rows={2} value={jobDescription} onChange={(e) => setJobDescription(e.target.value)} />

            <label>AI Provider:</label>
            <select value={provider} onChange={(e) => setProvider(e.target.value)}>
              <option value="openai">OpenAI</option>
              <option value="deepseek">Deepseek</option>
              <option value="google">Google</option>
              <option value="ollama">Ollama</option>
            </select>

            <label>Model:</label>
            <select value={model} onChange={(e) => setModel(e.target.value)}>
              {provider === "ollama"
                ? availableModels.map((mdl) => (
                    <option key={mdl} value={mdl}>
                      {mdl}
                    </option>
                  ))
                : (MODEL_MAP[provider] || []).map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
            </select>

            <label>API Key:</label>
            <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="Enter your API key" />

            <label>Token:</label>
            <input type="number" value={token} onChange={(e) => setToken(parseInt(e.target.value, 10) || 500)} placeholder="Number of tokens (default 500)" />

            <label>Temperature:</label>
            <input type="number" step="0.1" min="0" max="1" value={temperature} onChange={(e) => setTemperature(parseFloat(e.target.value))} />

            <label style={{ marginTop: "10px" }}>
              <input type="checkbox" checked={autoScroll} onChange={(e) => setAutoScroll(e.target.checked)} />
              Auto-Scroll
            </label>

            <div style={{ marginTop: "15px" }}>
              <button className="icon-button export-btn" style={{ marginRight: "10px" }} onClick={() => document.getElementById("import-settings").click()}>
                Import Settings
              </button>
              <input id="import-settings" type="file" accept=".json" style={{ display: "none" }} onChange={handleImportSettings} />

              <button className="icon-button export-btn" onClick={handleExportSettings}>
                Export Settings
              </button>
            </div>

            <button style={{ marginTop: "15px" }} onClick={(e) => { e.stopPropagation(); handleCloseSettings(); }}>
              Close
            </button>
          </div>
        </div>
      )}

      {showHistory && (
        <div className="history-panel" onClick={updateLastInteraction}>
          <button className="close-history" onClick={handleCloseHistory}>
            Close
          </button>
          <h2>Chat History</h2>
          {messages.length > 0 ? (
            messages.map((msg) => (
              <div key={msg.id} className="history-message">
                <span>{msg.role.toUpperCase()}: </span>
                {msg.content}
                <br />
                {showTime && <em>{msg.dateTime}</em>}
                {showTime && msg.role === "assistant" && msg.elapsedSec && (
                  <div>Response time: {msg.elapsedSec}s</div>
                )}
              </div>
            ))
          ) : (
            <p>No messages yet.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default App;
